import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

/** 'system' follows the device; 'light' / 'dark' are the user's choice on this device */
export type ThemeChoice = 'system' | 'light' | 'dark';
export type Theme = 'light' | 'dark';

const KEY = 'igoal.theme';
const media = () => window.matchMedia('(prefers-color-scheme: dark)');

function readChoice(): ThemeChoice {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}

export function resolveTheme(choice: ThemeChoice): Theme {
  return choice === 'system' ? (media().matches ? 'dark' : 'light') : choice;
}

/** Sets data-theme on <html>; index.html runs the same logic inline before React loads, so there is no flash */
export function applyTheme(theme: Theme) {
  document.documentElement.dataset.theme = theme;
  // match the phone header (surface colour), so the status bar and header read as one bar
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', theme === 'dark' ? '#1e232b' : '#ffffff');
}

const ThemeContext = createContext<{ choice: ThemeChoice; theme: Theme; setChoice: (c: ThemeChoice) => void }>({
  choice: 'system',
  theme: 'light',
  setChoice: () => undefined,
});

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [choice, setChoiceState] = useState<ThemeChoice>(readChoice);
  const [theme, setTheme] = useState<Theme>(() => resolveTheme(readChoice()));

  useEffect(() => {
    const update = () => {
      const t = resolveTheme(choice);
      setTheme(t);
      applyTheme(t);
    };
    update();
    if (choice !== 'system') return;
    const m = media();
    m.addEventListener('change', update);
    return () => m.removeEventListener('change', update);
  }, [choice]);

  const setChoice = useCallback((c: ThemeChoice) => {
    setChoiceState(c);
    try {
      if (c === 'system') localStorage.removeItem(KEY);
      else localStorage.setItem(KEY, c);
    } catch {
      // private mode: the choice lasts for this page only
    }
  }, []);

  const value = useMemo(() => ({ choice, theme, setChoice }), [choice, theme, setChoice]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => useContext(ThemeContext);
